import axiosInstance from "../utils/axios";
import {
  CvResponse,
  DeleteRequest,
  DeleteStatusResponse,
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  ProcessCvResponse,
  QuestionsResponse,
  RefreshResponse,
  RegisterRequest,
  RegisterResponse,
} from "../types/api";
import axiosPublicInstance from "../utils/refreshApi";

export const registerUser = async (
  userData: RegisterRequest
): Promise<RegisterResponse> => {
  try {
    const response = await axiosPublicInstance.post<RegisterResponse>(
      "api/users/register/",
      userData
    );
    return response.data;
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

export const loginUser = async (
  userData: LoginRequest
): Promise<LoginResponse> => {
  try {
    const response = await axiosPublicInstance.post<LoginResponse>(
      "api/users/login/",
      userData
    );
    return response.data;
  } catch (error) {
    console.error("Error registering user:", error);
    throw new Error("Failed to register user");
  }
};

export const logoutUser = async ({
  refresh_token,
}: LogoutRequest): Promise<any> => {
  try {
    const response = await axiosInstance.post("api/users/logout/", {
      refresh: refresh_token,
    });

    return response.data;
  } catch (error) {
    throw new Error("Failed to logout");
  }
};

export const deleteUserAccount = async ({
  password,
}: DeleteRequest): Promise<any> => {
  try {
    const response = password
      ? await axiosInstance.delete("api/users/delete-account/", {
          data: { password },
        })
      : await axiosInstance.delete("api/users/delete-account/");

    return response.data;
  } catch (error) {
    console.error("Delete account error:", error);
    throw new Error("Failed to delete account");
  }
};

export const deleteUserStatus = async (): Promise<DeleteStatusResponse> => {
  try {
    const response = await axiosInstance.get<DeleteStatusResponse>(
      "api/users/delete-account/"
    );

    return response.data;
  } catch (error) {
    console.error("Delete account error:", error);
    throw new Error("Failed to delete account");
  }
};

export const refreshUser = async ({ refresh_token }: LogoutRequest) => {
  try {
    const response = await axiosPublicInstance.post<RefreshResponse>(
      "api/users/token/refresh/",
      { refresh: refresh_token }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resendCode = async ({ user_id }: { user_id: number }) => {
  try {
    const response = await axiosPublicInstance.post(
      `api/users/${user_id}/resend-verification/`
    );

    return response.data;
  } catch (error) {
    console.error("Error resending email:", error);
    throw new Error("Failed to resend email");
  }
};

export const verifyCode = async ({
  code,
  user_id,
}: {
  code: string;
  user_id: number;
}) => {
  try {
    const response = await axiosPublicInstance.post(
      `api/users/${user_id}/verify-email/?code=${code}`
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const passwordRecovery = async (email: string) => {
  try {
    const response = await axiosInstance.post(`api/users/password/reset/`, {
      email,
    });
    return response.data;
  } catch (error) {
    console.error(`This email are not registered`, error);
    throw new Error(`Failed to recover password`);
  }
};

export const passwordRecoveryToken = async (token: string) => {
  try {
    const response = await axiosInstance.post(
      `api/users/password/reset/validate_token/`,
      {
        token,
      }
    );
    return response.data;
  } catch (error) {
    console.error(`This token is not valid`, error);
    throw new Error(`Failed to validate token`);
  }
};

export const newPassword = async ({
  token,
  password,
}: {
  token: string;
  password: string;
}) => {
  try {
    const response = await axiosInstance.post(
      `api/users/password/reset/confirm/`,
      {
        token,
        password,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadAvatar = async ({
  image,
  token,
}: {
  image: File;
  token: string;
}): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append("file", image);
    formData.append("file_name", image.name);

    const response = await axiosInstance.post(
      "/api/profiles/avatars/upload/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    throw new Error("Failed to upload avatar");
  }
};

export const uploadCV = async ({ cvFile }: { cvFile: File }) => {
  try {
    const formData = new FormData();

    formData.append("file", cvFile, cvFile.name);

    formData.append("file_name", cvFile.name);

    const response = await axiosInstance.post<CvResponse>(
      "/api/profiles/cvs/upload/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error uploading CV:", error);
    throw error;
  }
};

export const processCV = async ({
  cvId,
  replace_personal_data = true,
  replace_profile_data = true,
}: {
  cvId: number;
  replace_personal_data: boolean;
  replace_profile_data: boolean;
}) => {
  try {
    const response = await axiosInstance.post<ProcessCvResponse>(
      `/api/profiles/cvs/${cvId}/process/`,
      { replace_personal_data, replace_profile_data }
    );

    return response.data;
  } catch (error) {
    console.error("Error uploading CV:", error);
    throw new Error("Failed to upload CV");
  }
};

export const getQuestions = async (language: string = "en") => {
  try {
    const response = await axiosInstance.get<QuestionsResponse>(
      `/api/profiles/survey-questions/?lang=${language}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching questions:", error);
    throw new Error("Failed to get questions");
  }
};

export const sendAnswers = async (answers: {
  [key: string]: { answer_ids: number[]; text_answer?: string };
}) => {
  try {
    const response = await axiosInstance.post("/api/profiles/survey-answers/", {
      answers,
    });
    return response.data;
  } catch (error) {
    throw new Error("Failed to send answers");
  }
};
